import {useMemo, type ReactNode} from 'react';
import {CacheProvider} from '@emotion/react';
import createCache from '@emotion/cache';
import {ThemeProvider, createTheme, type Theme} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {usePopoutWindow} from './hooks/usePopoutWindow';

// Types
// -----------------------
interface MUIPopoutProviderProps {
  children: ReactNode;
  theme?: Theme;
  includeCssBaseline?: boolean;
}
// -----------------------

export function MUIPopoutProvider({
  children,
  theme,
  includeCssBaseline = true,
}: Readonly<MUIPopoutProviderProps>) {
  const {popoutDocument, isInPopout, popoutId} = usePopoutWindow();

  // Create emotion cache for the correct document
  const cache = useMemo(() => {
    const targetDoc = isInPopout ? popoutDocument : document;
    const cacheKey = isInPopout
      ? `mui-popout-${popoutId?.toLocaleLowerCase()}`
      : 'mui-main';

    return createCache({
      key: cacheKey,
      container: targetDoc.head,
      prepend: true,
    });
  }, [popoutDocument, isInPopout, popoutId]);

  const muiTheme = useMemo(() => theme || createTheme(), [theme]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={muiTheme}>
        {includeCssBaseline && <CssBaseline />}
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
